"use client";

import { cn } from "@istari-frontend/shared";
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";
import * as React from "react";
import { useCallback } from "react";

type ScrollbarsOption = "vertical" | "horizontal" | "both" | "none";

export function ScrollArea({
  className,
  children,
  scrollbars = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & { scrollbars?: ScrollbarsOption }) {
  // Horizontal scrolling needs Radix's default intrinsic-width sizing on the inner wrapper, so the
  // block/min-w-0 override below only applies when there's no horizontal scrollbar to preserve it for.
  const constrainsViewportOverflow = scrollbars === "vertical" || scrollbars === "none";

  const assertViewportShape = useCallback(
    (viewport: HTMLDivElement | null) => {
      if (!constrainsViewportOverflow || !viewport || !import.meta.env.DEV) return;

      const hasExpectedRadixShape = viewport.children.length === 1 && viewport.firstElementChild?.tagName === "DIV";
      if (!hasExpectedRadixShape) {
        console.error(
          "ScrollArea: Radix's internal Viewport wrapper DOM shape changed; the [&>div]:!block [&>div]:!min-w-0 override may no longer apply (see radix-ui/primitives#926).",
        );
      }
    },
    [constrainsViewportOverflow],
  );

  return (
    <ScrollAreaPrimitive.Root data-slot="scroll-area" className={cn("relative", className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        ref={assertViewportShape}
        data-slot="scroll-area-viewport"
        // Radix's inner Viewport wrapper has a hardcoded inline style (display:table; min-width:100%, no
        // override prop) that breaks truncate/min-w-0 on descendants (see radix-ui/primitives#926); `!` is
        // required to beat that inline style's specificity, not just a class-based default.
        className={cn(
          "focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1",
          constrainsViewportOverflow && "[&>div]:!block [&>div]:!min-w-0",
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {scrollbars !== "none" && (scrollbars === "vertical" || scrollbars === "both") ? (
        <ScrollBar orientation="vertical" />
      ) : null}
      {scrollbars === "both" || scrollbars === "horizontal" ? <ScrollBar orientation="horizontal" /> : null}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

export function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}
